import Invoice from '@/models/shop/InvoiceV2'
import FilesApiV2 from '@/services/misc/FilesApiV2'

/**
 * Mixin that provides printInvoice method for printing invoices by fetching HTML from the API.
 * Requires the component to have arenaId (computed or data).
 * If the component has printingInvoiceId in data, it will be set/cleared for loading state.
 */
export default {
  methods: {
    async printInvoice(invoice) {
      if (!this.arenaId || !invoice?.id) {
        return
      }
      if (this.printingInvoiceId !== undefined) {
        this.printingInvoiceId = invoice.id
      }
      try {
        let invoiceFileName = invoice.invoice_file_name

        if (!invoiceFileName) {
          const response = await Invoice.api.generateInvoiceFile(
            this.arenaId,
            invoice.id,
          )
          invoiceFileName = new Invoice(response.data).invoice_file_name
        }

        if (!invoiceFileName) {
          throw new Error('Invoice file name is missing')
        }

        const { data } = await FilesApiV2.downloadInvoice(
          this.arenaId,
          invoiceFileName,
        )
        const invoiceUrl = data?.url
        if (!invoiceUrl) {
          throw new Error('Invoice download URL is missing')
        }

        // Fetch HTML and write to a blank same-origin window for printing
        // Requires CORS to be configured on S3
        let htmlContent = null
        try {
          const response = await fetch(invoiceUrl, { mode: 'cors' })
          if (!response.ok) {
            throw new Error('Failed to fetch invoice HTML')
          }
          htmlContent = await response.text()
        } catch (fetchError) {
          // CORS not configured - fall back to opening in new tab
          console.warn(
            '[PrintInvoice] CORS fetch failed, opening in new tab:',
            fetchError,
          )
          window.open(invoiceUrl, '_blank')
          return
        }

        // Open blank window (same-origin) and write HTML content
        const printWindow = window.open('', '_blank')
        if (!printWindow) {
          console.error('[PrintInvoice] Popup blocked!')
          this.$store.dispatch('showSnackbar', {
            color: 'warning',
            message: 'Popup blocked. Please allow popups and try again.',
          })
          return
        }

        printWindow.document.open()
        printWindow.document.write(htmlContent)
        printWindow.document.close()

        // Wait for content to render, then print
        setTimeout(() => {
          try {
            printWindow.focus()
            printWindow.print()
          } catch (e) {
            console.error('[PrintInvoice] Print failed:', e)
          }
        }, 500)
      } catch (error) {
        const data = error?.response?.data
        let message = data?.message ?? 'Failed to print invoice.'
        const errors = []
        let iconAndColor = 'error'
        if (error?.response?.status === 422) {
          iconAndColor = 'warning'
          if (data?.errors) {
            for (const i in data.errors) {
              errors.push(`${i}: ${data.errors[i]}`)
            }
            message += ' ' + errors.join(', ')
          }
        }
        if (error?.response?.status === 500 && data?.errors) {
          for (const i in data.errors) {
            errors.push(data.errors[i])
          }
          message += ' ' + errors.join(', ')
        }
        this.$store.dispatch('showSnackbar', {
          color: iconAndColor,
          message,
          timeout: 10000,
          icon: iconAndColor,
        })
      } finally {
        if (this.printingInvoiceId !== undefined) {
          this.printingInvoiceId = null
        }
      }
    },
  },
}
